import * as React from "react";
import { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { Bookmark, Camera, Trash2, User, Image as ImageIcon, CheckCircle, AlertCircle, X } from "react-feather";
import { Button } from "semantic-ui-react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import defaultUserAvatar from "../assets/user.png";
import {
  uploadProfilePicture,
  removeProfilePicture,
} from "../api/authApi";
import useProfilePicture, { evictAvatarCache } from "../hooks/useProfilePicture";
import { userActions } from "../store/user/user-slice";
import ProfilePictureEditorModal from "./profile/ProfilePictureEditorModal";
import RemovePictureConfirmModal from "./profile/RemovePictureConfirmModal";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
};

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const ALLOWED_EXTENSIONS = ["jpg", "jpeg", "png", "webp"];

export default function UserAccount({ open, handleClose }) {
  const dispatch = useDispatch();
  const userDetail = useSelector((s) => s.user.userDetails);

  const loggedInUsername = useSelector((s) => s.user.username) || localStorage.getItem("USERNAME");

  const [tab, setTab] = useState(1); // 1: Profile Update
  const [profileSubTab, setProfileSubTab] = useState("picture"); // "picture" | "details"
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropperImageSrc, setCropperImageSrc] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("profile-picture.jpg");
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null); // { type: "success" | "error", text: string }

  const fileInputRef = useRef(null);

  /*
  const countryOptions = [
    { key: "af", value: "af", text: "Afghanistan" },
    { key: "ax", value: "ax", text: "Aland Islands" },
    { key: "al", value: "al", text: "Albania" },
    { key: "dz", value: "dz", text: "Algeria" },
    { key: "as", value: "as", text: "American Samoa" },
    { key: "ad", value: "ad", text: "Andorra" },
    { key: "ao", value: "ao", text: "Angola" },
    { key: "ai", value: "ai", text: "Anguilla" },
    { key: "ag", value: "ag", text: "Antigua" },
  ];
  */

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auto-dismiss in-modal status message after 5 seconds
  useEffect(() => {
    if (statusMessage) {
      const timer = setTimeout(() => {
        setStatusMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [statusMessage]);

  // Cleanup blob URLs on unmount or when cropper closes
  useEffect(() => {
    return () => {
      if (cropperImageSrc && cropperImageSrc.startsWith("blob:")) {
        URL.revokeObjectURL(cropperImageSrc);
      }
    };
  }, [cropperImageSrc]);

  // Reset states when main modal closes
  useEffect(() => {
    if (!open) {
      setCropperOpen(false);
      setIsRemoveModalOpen(false);
      setStatusMessage(null);
      if (cropperImageSrc && cropperImageSrc.startsWith("blob:")) {
        URL.revokeObjectURL(cropperImageSrc);
      }
      setCropperImageSrc(null);
    }
  }, [open, cropperImageSrc]);

  const showNotification = (type, message) => {
    setStatusMessage({ type, text: message });
    if (type === "success") {
      toast.success(message, { containerId: "Main" });
    } else {
      toast.error(message, { containerId: "Main" });
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    // 1. Extension check
    const ext = file.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      showNotification("error", "Only JPG, JPEG, PNG, and WEBP images are supported.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    // 2. MIME type check
    const validMimes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
    if (file.type && !validMimes.includes(file.type.toLowerCase())) {
      showNotification("error", "Only JPG, JPEG, PNG, and WEBP images are supported.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    // 3. File size check (5MB)
    if (file.size > MAX_FILE_SIZE) {
      showNotification("error", "Profile picture must be 5 MB or smaller.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    // Clear any previous status message
    setStatusMessage(null);

    // Revoke previous blob if any
    if (cropperImageSrc && cropperImageSrc.startsWith("blob:")) {
      URL.revokeObjectURL(cropperImageSrc);
    }

    const objectUrl = URL.createObjectURL(file);
    setCropperImageSrc(objectUrl);
    setSelectedFileName(file.name);
    setCropperOpen(true);

    // Reset input so re-selecting the same file triggers onChange
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSaveCroppedPicture = async (croppedFile) => {
    setIsSubmitting(true);
    const oldDocId = userDetail?.uploadDocumentSeq;
    const currentUserSeq = userDetail?.userSeq || userDetail?.id;
    const authorUsername = userDetail?.username || loggedInUsername || "user";
    try {
      const res = await uploadProfilePicture(
        croppedFile,
        currentUserSeq,
        authorUsername
      );

      const newDocId = res?.docId || res?.uploadDocumentSeq || res?.data?.docId;
      const successMsg = res?.message || "Profile picture updated successfully.";

      // Evict old cache and update redux user details locally without resetting permissions / whole dashboard
      evictAvatarCache(oldDocId);
      if (newDocId) {
        dispatch(userActions.updateProfilePicture(newDocId));
      }

      setCropperOpen(false);
      if (cropperImageSrc && cropperImageSrc.startsWith("blob:")) {
        URL.revokeObjectURL(cropperImageSrc);
      }
      setCropperImageSrc(null);

      showNotification("success", successMsg);
    } catch (e) {
      const errorMsg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to upload profile picture. Please try again.";
      showNotification("error", errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmRemove = async () => {
    setIsSubmitting(true);
    const oldDocId = userDetail?.uploadDocumentSeq;
    const currentUserSeq = userDetail?.userSeq || userDetail?.id;
    try {
      const res = await removeProfilePicture(currentUserSeq);
      const successMsg = res?.message || "Profile picture removed successfully.";

      // Evict cached blob and update redux state without unmounting the app
      evictAvatarCache(oldDocId);
      dispatch(userActions.updateProfilePicture(null));

      setIsRemoveModalOpen(false);
      showNotification("success", successMsg);
    } catch (e) {
      const errorMsg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to remove profile picture. Please try again.";
      showNotification("error", errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const savedAvatarUrl = useProfilePicture(userDetail?.uploadDocumentSeq);
  const hasCustomPhoto = Boolean(userDetail?.uploadDocumentSeq);
  const displayAvatar = savedAvatarUrl || defaultUserAvatar;

  // Generate initial letter for default avatar
  const userInitial = (
    userDetail?.firstName?.[0] ||
    userDetail?.username?.[0] ||
    "U"
  ).toUpperCase();

  const renderStatusBanner = () => {
    if (!statusMessage) return null;
    const isSuccess = statusMessage.type === "success";
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          padding: "10px 14px",
          borderRadius: "8px",
          marginBottom: "16px",
          backgroundColor: isSuccess ? "#f0fdf4" : "#fef2f2",
          border: `1px solid ${isSuccess ? "#bbf7d0" : "#fecaca"}`,
          color: isSuccess ? "#166534" : "#991b1b",
          fontSize: "13px",
          fontWeight: "500",
          animation: "fadeIn 0.2s ease-in-out",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {isSuccess ? (
            <CheckCircle size={16} color="#16a34a" />
          ) : (
            <AlertCircle size={16} color="#dc2626" />
          )}
          <span>{statusMessage.text}</span>
        </div>
        <button
          type="button"
          onClick={() => setStatusMessage(null)}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: "2px",
            color: isSuccess ? "#166534" : "#991b1b",
            display: "flex",
            alignItems: "center",
          }}
        >
          <X size={14} />
        </button>
      </div>
    );
  };

  const renderProfilePictureSubTab = () => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "20px 16px",
        backgroundColor: "#f8fafc",
        borderRadius: "10px",
        border: "1px solid #e2e8f0",
        textAlign: "center",
      }}
    >
      {/* Status Alert Banner */}
      <div style={{ width: "100%", maxWidth: "420px" }}>
        {renderStatusBanner()}
      </div>

      {/* Avatar Container */}
      <div
        style={{
          position: "relative",
          width: "120px",
          height: "120px",
          borderRadius: "50%",
          overflow: "hidden",
          border: "4px solid #ffffff",
          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
          marginBottom: "16px",
          backgroundColor: hasCustomPhoto ? "#f1f5f9" : "#1d4ed8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {hasCustomPhoto ? (
          <img
            src={displayAvatar}
            alt="Profile Avatar"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={(e) => {
              e.target.src = defaultUserAvatar;
            }}
          />
        ) : (
          <span
            style={{
              fontSize: "44px",
              fontWeight: "600",
              color: "#ffffff",
              userSelect: "none",
            }}
          >
            {userInitial}
          </span>
        )}
      </div>

      {/* Info Heading */}
      <div style={{ maxWidth: "340px", marginBottom: "18px" }}>
        <h4
          style={{
            margin: "0 0 6px",
            fontSize: "16px",
            fontWeight: "600",
            color: "#1e293b",
          }}
        >
          {hasCustomPhoto ? "Your profile picture" : "Add a profile picture"}
        </h4>
        <p
          style={{
            margin: 0,
            fontSize: "13px",
            color: "#64748b",
            lineHeight: "1.4",
          }}
        >
          A picture helps personalize your account and makes it easier for others to recognize you.
        </p>
      </div>

      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileSelect}
        style={{ display: "none" }}
        id="profile-pic-file-input"
        disabled={isSubmitting}
      />

      {/* Action Buttons */}
      <div
        style={{
          display: "flex",
          gap: "12px",
          alignItems: "center",
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <label htmlFor="profile-pic-file-input" style={{ margin: 0, cursor: "pointer" }}>
          <Button
            as="span"
            type="button"
            primary
            size="small"
            disabled={isSubmitting}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              borderRadius: "20px",
              padding: "9px 18px",
              backgroundColor: "#2563eb",
            }}
          >
            <Camera size={15} />
            {hasCustomPhoto ? "Change photo" : "Add photo"}
          </Button>
        </label>

        {hasCustomPhoto && (
          <Button
            type="button"
            basic
            color="red"
            size="small"
            onClick={() => setIsRemoveModalOpen(true)}
            disabled={isSubmitting}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              borderRadius: "20px",
              padding: "9px 16px",
            }}
          >
            <Trash2 size={15} /> Remove photo
          </Button>
        )}
      </div>

      <span
        style={{
          fontSize: "11px",
          color: "#94a3b8",
          marginTop: "14px",
        }}
      >
        Accepted: JPG, JPEG, PNG, WEBP • Max 5 MB
      </span>
    </div>
  );

  const renderProfileDetailsSubTab = () => (
    <div className="ui form" style={{ padding: "8px 4px" }}>
      <div className="two fields">
        <div className="field">
          <label style={{ color: "#334155", fontWeight: "600", fontSize: "13px" }}>First Name</label>
          <input
            type="text"
            value={userDetail?.firstName || "-"}
            readOnly
            style={{
              backgroundColor: "#ffffff",
              color: "#0f172a",
              borderColor: "#cbd5e1",
              fontWeight: "500",
              cursor: "default",
            }}
          />
        </div>
        <div className="field">
          <label style={{ color: "#334155", fontWeight: "600", fontSize: "13px" }}>Second Name</label>
          <input
            type="text"
            value={userDetail?.lastName || "-"}
            readOnly
            style={{
              backgroundColor: "#ffffff",
              color: "#0f172a",
              borderColor: "#cbd5e1",
              fontWeight: "500",
              cursor: "default",
            }}
          />
        </div>
      </div>
      <div className="two fields">
        <div className="field">
          <label style={{ color: "#334155", fontWeight: "600", fontSize: "13px" }}>Email</label>
          <input
            type="email"
            value={userDetail?.email || "-"}
            readOnly
            style={{
              backgroundColor: "#ffffff",
              color: "#0f172a",
              borderColor: "#cbd5e1",
              fontWeight: "500",
              cursor: "default",
            }}
          />
        </div>
        <div className="field">
          <label style={{ color: "#334155", fontWeight: "600", fontSize: "13px" }}>Contact Number</label>
          <input
            type="text"
            value={userDetail?.contactNo || "-"}
            readOnly
            style={{
              backgroundColor: "#ffffff",
              color: "#0f172a",
              borderColor: "#cbd5e1",
              fontWeight: "500",
              cursor: "default",
            }}
          />
        </div>
      </div>
    </div>
  );

  const renderSubTabsNavigation = () => (
    <div
      style={{
        display: "flex",
        gap: "8px",
        marginBottom: "16px",
        borderBottom: "1px solid #e2e8f0",
        paddingBottom: "10px",
      }}
    >
      <button
        type="button"
        onClick={() => {
          setProfileSubTab("picture");
          setStatusMessage(null);
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "6px 14px",
          borderRadius: "20px",
          border: "none",
          fontSize: "13px",
          fontWeight: profileSubTab === "picture" ? "600" : "500",
          backgroundColor: profileSubTab === "picture" ? "#eff6ff" : "transparent",
          color: profileSubTab === "picture" ? "#2563eb" : "#64748b",
          cursor: "pointer",
          transition: "all 0.15s ease",
        }}
      >
        <ImageIcon size={14} /> Profile picture
      </button>

      <button
        type="button"
        onClick={() => {
          setProfileSubTab("details");
          setStatusMessage(null);
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "6px 14px",
          borderRadius: "20px",
          border: "none",
          fontSize: "13px",
          fontWeight: profileSubTab === "details" ? "600" : "500",
          backgroundColor: profileSubTab === "details" ? "#eff6ff" : "transparent",
          color: profileSubTab === "details" ? "#2563eb" : "#64748b",
          cursor: "pointer",
          transition: "all 0.15s ease",
        }}
      >
        <User size={14} /> Profile details
      </button>
    </div>
  );

  return (
    <>
      {/* Large Screen Modal */}
      <Modal
        style={{ display: windowWidth <= 1580 ? "none" : "flex" }}
        open={open}
        onClose={handleClose}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
      >
        <Box
          sx={{
            ...style,
            width: "38vw",
            maxWidth: "600px",
            minWidth: "480px",
            padding: "16px",
            border: "1px solid rgb(218, 218, 218)",
            outline: "none",
            borderRadius: "10px",
          }}
        >
          <div className="user-account-heading" style={{ textAlign: "center" }}>
            <div
              style={{
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                overflow: "hidden",
                margin: "0 auto 8px",
                backgroundColor: hasCustomPhoto ? "#f1f5f9" : "#1d4ed8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {hasCustomPhoto ? (
                <img
                  src={displayAvatar}
                  alt="user"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={(e) => {
                    e.target.src = defaultUserAvatar;
                  }}
                />
              ) : (
                <span style={{ fontSize: "24px", fontWeight: "600", color: "#fff" }}>
                  {userInitial}
                </span>
              )}
            </div>
            <span style={{ fontWeight: "bold", fontSize: "16px", display: "block" }}>
              {userDetail?.firstName
                ? `${userDetail?.firstName} ${userDetail?.lastName || ""}`
                : userDetail?.username || "User Name"}
            </span>
            <span style={{ color: "#666", fontSize: "13px", display: "block" }}>
              {userDetail?.email || "sample@gmail.com"}
            </span>
          </div>
          <div className="ui divider"></div>
          <Card style={{ boxShadow: "none" }}>
            <CardContent style={{ padding: "0px" }}>
              <div className="ui top attached tabular menu">
                <div
                  className={`item ${tab === 1 ? "active" : ""}`}
                  onClick={() => setTab(1)}
                  style={{ cursor: "pointer" }}
                >
                  <Bookmark size={15} style={{ marginRight: "5px" }} />
                  Profile Update
                </div>
                {/* 
                <div
                  className={`item ${tab === 2 ? "active" : ""}`}
                  onClick={() => setTab(2)}
                  style={{ cursor: "pointer" }}
                >
                  <Bookmark size={15} style={{ marginRight: "5px" }} />
                  Password Change
                </div>
                <div
                  className={`item ${tab === 3 ? "active" : ""}`}
                  onClick={() => setTab(3)}
                  style={{ cursor: "pointer" }}
                >
                  <Bookmark size={15} style={{ marginRight: "5px" }} />
                  Customer Setting
                </div>
                */}
              </div>
              <div
                className="ui bottom attached active tab segment"
                style={{ display: tab === 1 ? "block" : "none" }}
              >
                {/* 2 Sub-Tabs inside Profile Update */}
                {renderSubTabsNavigation()}

                {profileSubTab === "picture"
                  ? renderProfilePictureSubTab()
                  : renderProfileDetailsSubTab()}
              </div>
              {/* 
              <div
                className="ui bottom attached active tab segment"
                style={{ display: tab === 2 ? "block" : "none" }}
              >
                <form className="ui form">
                  <div className="field">
                    <label>Current Password</label>
                    <input type="password" />
                  </div>
                  <div className="field">
                    <label>New Password</label>
                    <input type="password" />
                  </div>
                  <div className="field">
                    <label>Confirm New Password</label>
                    <input type="password" />
                  </div>
                  <Button type="submit" className="ui button">
                    Change Password
                  </Button>
                </form>
              </div>
              <div
                className="ui bottom attached active tab segment"
                style={{ display: tab === 3 ? "block" : "none" }}
              >
                <form className="ui form" style={{ height: "263px" }}>
                  <div className="field">
                    <div className="two fields">
                      <div className="field">
                        <label>Customer Group</label>
                        <Select
                          clearable
                          placeholder="Customer Group"
                          options={countryOptions}
                          style={{ height: "5px" }}
                        />
                      </div>
                      <div className="field">
                        <label>Customer</label>
                        <Select
                          clearable
                          placeholder="Customer"
                          options={countryOptions}
                          style={{ height: "5px" }}
                        />
                      </div>
                    </div>
                  </div>
                  <Button type="submit" className="ui button">
                    Update
                  </Button>
                </form>
              </div>
              */}
            </CardContent>
          </Card>
        </Box>
      </Modal>

      {/* Small Screen Modal */}
      <Modal
        style={{ display: windowWidth <= 1580 ? "flex" : "none" }}
        open={open}
        onClose={handleClose}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
      >
        <Box
          sx={{
            ...style,
            width: "90vw",
            maxWidth: "520px",
            padding: "12px",
            border: "1px solid rgb(218, 218, 218)",
            outline: "none",
            borderRadius: "8px",
          }}
        >
          <div className="user-account-heading" style={{ textAlign: "center" }}>
            <div
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "50%",
                overflow: "hidden",
                margin: "0 auto 6px",
                backgroundColor: hasCustomPhoto ? "#f1f5f9" : "#1d4ed8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {hasCustomPhoto ? (
                <img
                  src={displayAvatar}
                  alt="user"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={(e) => {
                    e.target.src = defaultUserAvatar;
                  }}
                />
              ) : (
                <span style={{ fontSize: "20px", fontWeight: "600", color: "#fff" }}>
                  {userInitial}
                </span>
              )}
            </div>
            <span style={{ fontWeight: "bold", display: "block" }}>
              {userDetail?.firstName
                ? `${userDetail?.firstName} ${userDetail?.lastName || ""}`
                : userDetail?.username || "User Name"}
            </span>
            <span style={{ color: "#666", fontSize: "12px", display: "block" }}>
              {userDetail?.email || "sample@gmail.com"}
            </span>
          </div>
          <div className="ui divider"></div>
          <div style={{ maxHeight: "72vh", overflowY: "auto" }}>
            <div
              style={{
                padding: "10px",
                border: "1px solid rgb(218, 218, 218)",
                borderRadius: "5px",
                marginBottom: "10px",
              }}
            >
              <h4 className="ui dividing header" style={{ marginTop: 0 }}>
                Profile Update
              </h4>

              {/* 2 Sub-Tabs inside Profile Update for small screens */}
              {renderSubTabsNavigation()}

              {profileSubTab === "picture"
                ? renderProfilePictureSubTab()
                : renderProfileDetailsSubTab()}
            </div>
          </div>
        </Box>
      </Modal>

      {/* Google-style Photo Crop/Rotate Editor Modal */}
      <ProfilePictureEditorModal
        open={cropperOpen}
        imageSrc={cropperImageSrc}
        fileName={selectedFileName}
        onClose={() => {
          setCropperOpen(false);
          if (cropperImageSrc && cropperImageSrc.startsWith("blob:")) {
            URL.revokeObjectURL(cropperImageSrc);
          }
          setCropperImageSrc(null);
        }}
        onSave={handleSaveCroppedPicture}
        isSaving={isSubmitting}
      />

      {/* Remove Picture Confirmation Modal */}
      <RemovePictureConfirmModal
        open={isRemoveModalOpen}
        onClose={() => setIsRemoveModalOpen(false)}
        onConfirm={handleConfirmRemove}
        isRemoving={isSubmitting}
      />
    </>
  );
}
